// src/pages/pedidos/EditarPedido.js
import React, { useCallback } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import { Offcanvas, Badge, Card, Nav, Tab, Alert } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import { toast } from 'react-toastify'
import api from 'api/api'

import { usePedidoForm } from 'hooks/usePedidoForm'
import {
	getTipoColor,
	OPCIONES_TIPOS_PEDIDO_PARA_FILTRO,
	calcularEstados,
	requiereEnvio
} from 'config/pedidos'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { getDisplayDateTime } from 'utils/date'

// Importar los componentes de tabs
import {
	GeneralTab,
	EstadosTab,
	ItemsTab,
	EntregaTab,
	FacturacionTab
} from 'components/PedidoTabs'

function EditarPedido(props) {
	const { pedidoOriginal, mostrar, guardar, cancelar, onRefresh } = props

	// Función para extraer el pedido real del objeto anidado
	const extraerPedidoReal = React.useCallback((pedidoData) => {
		if (!pedidoData) return null

		// Debería llegar directamente como pedido válido
		if (pedidoData.id) {
			return pedidoData
		}

		console.log('❌ No se pudo extraer el pedido - estructura:', Object.keys(pedidoData))
		return null
	}, [])

	// Función para normalizar pedido antes de pasarlo al hook
	const normalizarPedidoParaEditar = React.useCallback((pedidoData) => {
		// Primero extraer el pedido real
		const pedido = extraerPedidoReal(pedidoData)

		if (!pedido) return null

		const pedidoNormalizado = {
			...pedido,
			// Preservar costoEnvioManual del pedido (viene como boolean del backend)
			costoEnvioManual: pedido.costoEnvioManual || false,
			puntoEntrega: pedido.puntoEntrega ? {
				...pedido.puntoEntrega,
				// Normalizar ciudad: si es objeto, extraer nombre; si es string, mantener
				ciudad: typeof pedido.puntoEntrega.ciudad === 'string'
					? pedido.puntoEntrega.ciudad
					: pedido.puntoEntrega.ciudad?.nombre || '',
				// Normalizar país: si es objeto, extraer nombre; si es string, mantener  
				pais: typeof pedido.puntoEntrega.pais === 'string'
					? pedido.puntoEntrega.pais
					: pedido.puntoEntrega.pais?.nombre || '',
				// Normalizar barrio: si es objeto, extraer nombre; si es string, mantener
				barrio: pedido.puntoEntrega.barrio?.nombre || pedido.puntoEntrega.barrio || '',
				// Asegurar que areaId sea null si no existe (para validación inicial)
				areaId: pedido.puntoEntrega.areaId || null,
				// El costoEnvio viene del pedido, no del puntoEntrega
				costoEnvio: pedido.costoEnvio || 0
			} : null,
			datosFacturacion: {
				razonSocial: pedido.datosFacturacion?.razonSocial || '',
				ruc: pedido.datosFacturacion?.ruc || ''
			},
			observaciones: pedido.observaciones || ''
		}

		return pedidoNormalizado
	}, [])

	// Normalizar pedido original - SOLO si existe
	const pedidoNormalizado = React.useMemo(() => {
		if (!pedidoOriginal || !mostrar) {
			console.log('❌ No hay pedido original o no se está mostrando')
			return null
		}

		return normalizarPedidoParaEditar(pedidoOriginal)
	}, [pedidoOriginal, mostrar, normalizarPedidoParaEditar])

	// ✅ Función para cambios INMEDIATOS de estado (como en Pedidos.js)
	const cambiarEstadoInmediato = useCallback(async (campo, valor) => {
		if (!pedidoNormalizado?.id) return

		try {
			let endpoint
			let datos

			if (campo === 'pagado') {
				// Cambio de estado de pago
				endpoint = `a/pedidos/${pedidoNormalizado.id}/pagado`
				datos = { pagado: valor }
			} else if (campo === 'finalizado') {
				// Cambio de finalización
				endpoint = `a/pedidos/${pedidoNormalizado.id}/finalizado`
				datos = { finalizado: valor }
			} else {
				// Cambio de estado principal - enviar como boolean
				endpoint = `a/pedidos/${pedidoNormalizado.id}/estado`
				datos = {
					campo,
					valor: valor !== null // Convertir fecha a boolean: si no es null = true, si es null = false
				}
			}

			const { msg } = await api.post(endpoint, datos)

			// Mostrar mensaje de éxito
			toast.success(msg || 'Estado actualizado correctamente')

			// Refrescar la lista ANTES de cerrar
			if (typeof onRefresh === 'function') {
				await onRefresh()
			}

			// Cerrar el panel después de refrescar
			if (typeof cancelar === 'function') {
				cancelar()
			}

		} catch (error) {
			console.error('Error al cambiar estado:', error)
			toast.error('Error al cambiar el estado del pedido')
		}
	}, [pedidoNormalizado?.id, cancelar, onRefresh]) // Agregar onRefresh a dependencias

	// Usar el hook personalizado para manejar todo el estado del formulario
	const pedidoForm = usePedidoForm({
		modo: 'editar',
		pedidoOriginal: pedidoNormalizado, // Pasar el pedido normalizado
		onSave: guardar,
		onCancel: cancelar
	})

	const {
		loading,
		erroresValidacion,
		activeTab,
		setActiveTab,
		datosGenerales,
		cliente,
		items,
		itemsModificados,
		puntoEntrega,
		datosFacturacion,
		costoEnvioManual,
		totales, // Totales calculados automáticamente
		handleDatosGeneralesChange,
		handleClienteChange,
		handleItemsChange,
		handlePuntoEntregaChange,
		handleDatosFacturacionChange,
		handleCostoEnvioManualChange,
		handleSave,
		handleCancel,
		hayCambios,
		inicializado // Flag de inicializado
	} = pedidoForm

	// Funciones auxiliares
	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	const calcularCantidadItems = () => {
		return totales.cantidadItems || 0
	}

	const esPedidoPagado = () => {
		return pedidoNormalizado ? calcularEstados(pedidoNormalizado).pagado : false
	}

	// Si no hay pedido, no renderizar nada
	if (!mostrar || !pedidoNormalizado) {
		return null
	}

	return (
		<Offcanvas
			show={mostrar}
			onHide={handleCancel}
			placement="end"
			style={{ width: '80%' }}
			className="editar-pedido-panel"
		>
			<PanelHeader
				onClick={handleCancel}
				iconClass="fa-arrow-left"
				btnText="Atrás"
				text={`Editar Pedido #${pedidoNormalizado.id}`}
			/>

			<Offcanvas.Body>
				{/* Header con información resumida del pedido */}
				<Card className="mb-4 shadow-sm">
					<Card.Body>
						<Row>
							<Col sm={3}>
								<div className="text-center">
									<h4 className="text-primary mb-0">#{pedidoNormalizado.id}</h4>
									<small className="text-muted">ID del Pedido</small>
								</div>
							</Col>
							<Col sm={3}>
								<div className="text-center">
									<h5 className="text-success mb-0">{formatearPrecio(totales.total)}</h5>
									<small className="text-muted">Total</small>
									{hayCambios && totales.total !== (pedidoNormalizado.montoTotal || 0) && (
										<div className="mt-1">
											<small className="text-warning">
												<i className="fa fa-exclamation-triangle me-1"></i>
												Total modificado
											</small>
										</div>
									)}
								</div>
							</Col>
							<Col sm={3}>
								<div className="text-center">
									<Badge bg={getTipoColor(datosGenerales.tipo)} className="mb-1">
										{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.find(t => t.value === datosGenerales.tipo)?.label || datosGenerales.tipo}
									</Badge>
									<br />
									<small className="text-muted">Tipo</small>
								</div>
							</Col>
							<Col sm={3}>
								<div className="text-center">
									<div className="mb-1">{getDisplayDateTime(pedidoNormalizado.recibido)}</div>
									<small className="text-muted">Fecha recepción</small>
								</div>
							</Col>
						</Row>

						{/* Mostrar desglose de totales si hay cambios */}
						{hayCambios && (totales.subtotal > 0 || totales.costoEnvio > 0) && (
							<div className="mt-3 pt-3 border-top">
								<Row className="text-sm">
									<Col>
										<strong>Desglose actualizado:</strong>
									</Col>
								</Row>
								<Row className="text-sm">
									<Col>Subtotal ({calcularCantidadItems()} items):</Col>
									<Col className="text-end">{formatearPrecio(totales.subtotal)}</Col>
								</Row>
								{totales.costoEnvio > 0 && (
									<Row className="text-sm">
										<Col>Costo de envío:</Col>
										<Col className="text-end">{formatearPrecio(totales.costoEnvio)}</Col>
									</Row>
								)}
								<Row className="text-sm border-top pt-1">
									<Col><strong>Total:</strong></Col>
									<Col className="text-end"><strong>{formatearPrecio(totales.total)}</strong></Col>
								</Row>
							</div>
						)}
					</Card.Body>
				</Card>

				{/* Alerta si hay cambios en totales de pedido pagado */}
				{esPedidoPagado() && hayCambios && totales.total !== (pedidoNormalizado.montoTotal || 0) && (
					<Alert variant="warning" className="mb-4">
						<i className="fa fa-exclamation-triangle me-2"></i>
						<strong>¡Atención!</strong> Este pedido ya fue pagado por <strong>{formatearPrecio(pedidoNormalizado.montoTotal)}</strong>,
						pero el nuevo total es <strong>{formatearPrecio(totales.total)}</strong>.
						Verifique que el cambio sea correcto antes de guardar.
					</Alert>
				)}

				{/* Navegación por pestañas */}
				<Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
					<Nav variant="tabs" className="mb-4">
						<Nav.Item>
							<Nav.Link eventKey="general">
								<i className="fa fa-info-circle me-2"></i>
								General
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="estados">
								<i className="fa fa-flag me-2"></i>
								Estados
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="items">
								<i className="fa fa-shopping-cart me-2"></i>
								Items ({calcularCantidadItems()})
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link
								eventKey="entrega"
								disabled={!requiereEnvio(datosGenerales.tipo)}
							>
								<i className="fa fa-map-marker-alt me-2"></i>
								Entrega
								{!requiereEnvio(datosGenerales.tipo) && (
									<small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
										No aplica
									</small>
								)}
								{requiereEnvio(datosGenerales.tipo) && totales.costoEnvio > 0 && (
									<small className="d-block text-success" style={{ fontSize: '0.7rem' }}>
										{formatearPrecio(totales.costoEnvio)}
									</small>
								)}
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="facturacion">
								<i className="fa fa-file-invoice me-2"></i>
								Facturación
							</Nav.Link>
						</Nav.Item>
					</Nav>

					<Tab.Content>
						{/* Solo renderizar las pestañas cuando esté inicializado */}
						{!inicializado ? (
							<div className="text-center py-5">
								<i className="fa fa-spinner fa-spin fa-2x text-primary mb-3"></i>
								<p className="text-muted">Cargando datos del pedido...</p>
							</div>
						) : (
							<>
								{/* Pestaña General */}
								<Tab.Pane eventKey="general">
									<GeneralTab
										data={datosGenerales}
										onChange={handleDatosGeneralesChange}
										cliente={cliente}
										onClienteChange={handleClienteChange}
										disabled={loading}
										clienteReadOnly={esPedidoPagado()}
										clienteReadOnlyReason="No se puede cambiar el cliente de un pedido pagado"
										erroresValidacion={erroresValidacion}
									/>
								</Tab.Pane>

								{/* Pestaña Estados */}
								<Tab.Pane eventKey="estados">
									<EstadosTab
										pedido={pedidoNormalizado} // Usar pedido original (se actualizará automáticamente)
										onEstadoChange={cambiarEstadoInmediato} // Usar función de cambio inmediato
										disabled={loading}
									/>
								</Tab.Pane>

								{/* Pestaña Items */}
								<Tab.Pane eventKey="items">
									<ItemsTab
										items={items}
										itemsModificados={itemsModificados}
										onItemsChange={handleItemsChange}
										disabled={loading}
										showPaymentWarning={esPedidoPagado()}
										modo="editar"
										totales={totales} // Pasar totales para mostrar información actualizada
									/>
								</Tab.Pane>

								{/* Pestaña Entrega */}
								<Tab.Pane eventKey="entrega">
									<EntregaTab
										tipoPedido={datosGenerales.tipo}
										puntoEntrega={puntoEntrega}
										onChange={handlePuntoEntregaChange}
										disabled={loading}
										showPaymentWarning={esPedidoPagado()}
										costoEnvioManual={costoEnvioManual}
										onCostoEnvioManualChange={handleCostoEnvioManualChange}
									/>
								</Tab.Pane>

								{/* Pestaña Facturación */}
								<Tab.Pane eventKey="facturacion">
									<FacturacionTab
										datosFacturacion={datosFacturacion}
										datosOriginales={pedidoNormalizado?.datosFacturacion}
										onChange={handleDatosFacturacionChange}
										disabled={loading || esPedidoPagado()}
										showPaymentWarning={esPedidoPagado()}
										erroresValidacion={erroresValidacion}
									/>
								</Tab.Pane>
							</>
						)}
					</Tab.Content>
				</Tab.Container>
			</Offcanvas.Body>

			<PanelFooter>
				<div className="d-flex justify-content-between align-items-center w-100">
					<Button
						variant="outline-secondary"
						onClick={handleCancel}
						disabled={loading}
					>
						Cancelar
					</Button>

					<div className="d-flex align-items-center">
						{hayCambios && (
							<small className="text-muted me-3">
								<i className="fa fa-edit me-1"></i>
								Cambios sin guardar
							</small>
						)}
						{totales.total !== (pedidoNormalizado.montoTotal || 0) && (
							<small className="text-primary me-3">
								Total: {formatearPrecio(totales.total)}
							</small>
						)}
						<Button
							variant="primary"
							onClick={handleSave}
							disabled={loading || !hayCambios}
						>
							{loading ? (
								<>
									<i className="fa fa-spinner fa-spin me-2"></i>
									Guardando...
								</>
							) : (
								<>
									<i className="fa fa-save me-2"></i>
									Guardar Cambios
								</>
							)}
						</Button>
					</div>
				</div>
			</PanelFooter>
		</Offcanvas>
	)
}

export default EditarPedido